/**
 * Composant Bottom Bar
 * Barre d'annonce fixe en bas de l'écran avec texte, CTA et bouton de fermeture
 * Inspiré par le design de Typology
 */
class BottomBar extends HTMLElement {
    constructor() {
        super();
        this.closeButton = this.querySelector('.bottom-bar__close');
        this.ctaButton = this.querySelector('.bottom-bar__button');
        this.bottomBar = this;
        this.sectionId = this.dataset.sectionId;

        // Vérifier si la barre doit être affichée
        this.checkAndShowBar();

        // Ajouter l'événement de fermeture sur le bouton de fermeture
        this.closeButton.addEventListener('click', this.closeBar.bind(this));

        // Ajouter l'événement de fermeture sur le CTA
        if (this.ctaButton) {
            this.ctaButton.addEventListener('click', this.handleCtaClick.bind(this));
        }
    }

    // Méthode pour obtenir l'élément product-sticky-form
    getProductStickyForm() {
        return document.querySelector('.product-sticky-form');
    }

    // Méthode utilitaire pour attendre qu'un élément soit disponible dans le DOM
    waitForElement(selector, timeout = 15000) {
        return new Promise((resolve) => {
            // Vérifier d'abord si l'élément existe déjà
            const existingElement = document.querySelector(selector);
            if (existingElement) {
                resolve(existingElement);
                return;
            }

            // Créer un MutationObserver pour détecter l'ajout de l'élément
            const observer = new MutationObserver((mutations) => {
                const element = document.querySelector(selector);
                if (element) {
                    observer.disconnect();
                    resolve(element);
                }
            });

            // Observer les changements dans le DOM
            observer.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeOldValue: true
            });

            // Timeout de sécurité
            setTimeout(() => {
                observer.disconnect();
                resolve(null);
            }, timeout);
        });
    }

    async getAssistanceLauncher() {
        // Chercher d'abord l'iframe avec l'id launcher
        let element = document.querySelector('#launcher');
        if (element) return element;

        // Fallback: chercher l'élément avec data-testid="launcher"
        element = document.querySelector('[data-testid="launcher"]');
        if (element) return element;

        // Attendre que l'élément soit injecté par l'app
        return await this.waitForElement('#launcher');
    }

    // Méthode pour vérifier si la bottom-bar est visible
    isBarVisible() {
        // Vérifier si l'élément existe dans le DOM
        if (!this.bottomBar) return false;

        // Vérifier si la classe 'hidden' est présente
        if (this.bottomBar.classList.contains('hidden')) return false;

        // Vérifier les styles inline
        const style = window.getComputedStyle(this.bottomBar);
        if (style.display === 'none' || style.visibility === 'hidden') return false;

        // Vérifier si l'élément a une hauteur visible
        const rect = this.bottomBar.getBoundingClientRect();
        if (rect.height === 0 || rect.width === 0) return false;

        return true;
    }

    async checkAndShowBar() {
        const closedBars = this.getCookie('closed_bottom_bars');
        // Si aucun cookie n'existe ou si cette section n'est pas dans le cookie, on affiche la barre
        if (!closedBars || !closedBars.includes(this.sectionId)) {
            this.bottomBar.classList.remove('hidden');
            this.closeButton.classList.remove('hidden');

            // Attendre un moment pour le chargement
            await new Promise(resolve => setTimeout(resolve, 3000));

            // Vérifier que la bottom-bar est bien visible avant d'appliquer les modifications
            if (!this.isBarVisible()) {
                return;
            }

            const productStickyForm = this.getProductStickyForm();
            if (productStickyForm) {
                productStickyForm.style.display = 'none';
            }

            // Recherche simple de l'iframe launcher
            const assistanceLauncher = await this.getAssistanceLauncher();

            if (assistanceLauncher) {
                assistanceLauncher.style.display = 'none';
                assistanceLauncher.style.visibility = 'hidden';
                assistanceLauncher.setAttribute('hidden', 'true');

                // Sauvegarder la référence
                this.assistanceLauncher = assistanceLauncher;
            }
        }
    }

    async closeBar() {
        this.bottomBar.classList.add('hidden');
        this.closeButton.classList.add('hidden');

        const productStickyForm = this.getProductStickyForm();
        if (productStickyForm) {
            productStickyForm.style.display = '';
        }

        // Réafficher l'assistance launcher
        if (this.assistanceLauncher) {
            this.assistanceLauncher.style.display = '';
            this.assistanceLauncher.style.visibility = '';
            this.assistanceLauncher.removeAttribute('hidden');
        }

        this.saveClosed();
    }

    // Gère le clic sur le CTA
    handleCtaClick(event) {
        // Ne pas bloquer la navigation
        // event.preventDefault();

        // Sauvegarder que la barre a été fermée
        this.saveClosed();

        // La navigation se fera normalement puisqu'on n'a pas empêché le comportement par défaut
    }

    saveClosed() {
        let closedBars = this.getCookie('closed_bottom_bars') || '';
        const closedArray = closedBars ? closedBars.split(',') : [];

        if (!closedArray.includes(this.sectionId)) {
            closedArray.push(this.sectionId);
        }

        // La durée d'expiration est définie dans la section via section.settings.days_before_reappear
        // Nous la récupérons via un attribut data sur l'élément
        const daysBeforeReappear = parseInt(this.dataset.daysBeforeReappear || 7, 10);
        const expiryDate = new Date();
        expiryDate.setTime(expiryDate.getTime() + (daysBeforeReappear * 24 * 60 * 60 * 1000));

        document.cookie = 'closed_bottom_bars=' + closedArray.join(',') + '; expires=' + expiryDate.toUTCString() + '; path=/';
    }

    getCookie(name) {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        if (match) return match[2];
        return null;
    }
}

// Définir le custom element si ce n'est pas déjà fait
if (!customElements.get('bottom-bar')) {
    customElements.define('bottom-bar', BottomBar);
} 